import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { posBackendApi } from '@/lib/api-client';

export interface Customer {
  id: string;
  tenantId: string;
  phone: string;
  phoneNormalized: string | null;
  firstName: string | null;
  lastName: string | null;
  name: string;
  email: string | null;
  birthday: string | null;
  emailOptIn: boolean;
  smsOptIn: boolean;
  customerType: 'retail' | 'credit' | 'wholesale';
  creditLimit: string;
  currentBalance: string;
  businessName: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  loyaltyPoints: number;
  totalVisits: number;
  totalSpent: string;
  signupDate: string;
  lastVisitAt: string | null;
  homeLocationId: string | null;
  /** Where the customer originally signed up (pos-backend resolves it). */
  signupLocationId: string | null;
  signupLocationName: string | null;
  notes: string | null;
  updatedAt: string;
}

export type CustomerSortBy = 'loyaltyPoints' | 'currentBalance' | 'creditLimit';

export interface CustomerListParams {
  search?: string;
  customerType?: 'retail' | 'credit' | 'wholesale';
  limit?: number;
  offset?: number;
  /** Server-side sort (whitelisted in pos-backend); default is most recently updated first. */
  sortBy?: CustomerSortBy;
  sortDir?: 'asc' | 'desc';
}

export interface CustomerListResponse {
  data: Customer[];
  meta: { total: number; limit: number; offset: number };
}

export interface PatchCustomerInput {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  birthday?: string | null;
  notes?: string | null;
  emailOptIn?: boolean;
  smsOptIn?: boolean;
  customerType?: 'retail' | 'credit' | 'wholesale';
  creditLimit?: number;
  businessName?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  homeLocationId?: string | null;
}

export interface CreateCustomerInput {
  phone: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  birthday?: string;
  notes?: string;
  emailOptIn?: boolean;
  smsOptIn?: boolean;
  customerType?: 'retail' | 'credit' | 'wholesale';
  creditLimit?: number;
  businessName?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  /**
   * Location this customer enrolled at. Required for the welcome SMS to
   * fire — the per-location super-admin Customer Engagement gate is
   * checked against this id. The dashboard create dialog auto-fills it
   * from the header location picker.
   */
  homeLocationId?: string;
}

const KEY = ['customer-engagement', 'customers'] as const;

export function useCustomers(params: CustomerListParams = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...KEY, params],
    queryFn: () =>
      posBackendApi.get<CustomerListResponse>('/customer-engagement/customers', {
        search: params.search,
        customerType: params.customerType,
        limit: params.limit ?? 50,
        offset: params.offset ?? 0,
        sortBy: params.sortBy,
        sortDir: params.sortBy ? params.sortDir : undefined,
      }),
    enabled: options.enabled ?? true,
    staleTime: 30_000,
  });
}

export function useCustomer(id: string | undefined, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...KEY, 'detail', id],
    queryFn: () => posBackendApi.get<{ data: Customer }>(`/customer-engagement/customers/${id}`),
    enabled: (options.enabled ?? true) && !!id,
  });
}

export function useUpdateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; patch: PatchCustomerInput }) =>
      posBackendApi.patch<{ data: Customer }>(`/customer-engagement/customers/${vars.id}`, vars.patch),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: [...KEY, 'detail', vars.id] });
    },
  });
}

export function useCreateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateCustomerInput) =>
      posBackendApi.post<{ data: Customer }>('/customer-engagement/customers', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}
